import React, { useEffect, useState } from "react";
import { IndianRupee, TrendingUp, TrendingDown, ArrowRight, AlertTriangle } from "lucide-react";
import api from "../lib/api";

/**
 * Wave U — compact dashboard widget for project cost health.
 * Renders on the Overview tab. Pulls from /api/cost/dashboard-summary/{id}
 * which already aggregates the latest estimate, benchmark delta, top-5
 * line items and a live-market summary (rising / falling commodity list).
 */
export default function CostDashboardWidget({ projectId, onOpenCost }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    if (!projectId) return;
    api.get(`/cost/dashboard-summary/${projectId}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || e.message); });
    return () => { alive = false; };
  }, [projectId]);

  if (err) {
    return (
      <div className="pf-card" data-testid="cost-widget-err" style={{ padding: 12, fontSize: 12, color: "var(--pf-err)" }}>
        Cost widget unavailable: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="pf-card" style={{ padding: 12, fontSize: 12, color: "var(--pf-text-muted)" }}>Loading cost snapshot…</div>
    );
  }

  if (!data.has_estimate) {
    return (
      <div className="pf-card" data-testid="cost-widget-empty" style={{ padding: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <IndianRupee size={16} color="var(--pf-spark)" />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="pf-overline" style={{ marginBottom: 2 }}>COST · NO ESTIMATE YET</div>
          <div style={{ fontSize: 12.5, color: "var(--pf-text-muted)" }}>
            {data.hint || "Seed the cost widget by running an estimate."}
          </div>
        </div>
        {data.market_summary?.rising_items?.length > 0 && (
          <div style={{ fontSize: 11, color: "var(--pf-text-muted)" }}>
            <TrendingUp size={11} color="var(--pf-err)" /> Watch: {data.market_summary.rising_items.slice(0, 3).join(", ")}
          </div>
        )}
        {onOpenCost && (
          <button type="button" className="btn btn-primary" onClick={onOpenCost} data-testid="cost-widget-open">
            Open Cost Tab <ArrowRight size={13} />
          </button>
        )}
      </div>
    );
  }

  const inr = (v) => (v == null ? "—" : `₹ ${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
  const bench = data.benchmark;
  const overBench = bench?.delta_pct != null && bench.delta_pct > 0;
  return (
    <div className="pf-card" data-testid="cost-widget" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <IndianRupee size={16} color="var(--pf-spark)" />
        <div className="pf-overline">PROJECT COST SNAPSHOT</div>
        <span style={{ fontSize: 10, color: "var(--pf-text-dim)", fontFamily: "'JetBrains Mono', monospace" }}>
          saved {data.saved_at?.slice(0, 16)?.replace("T", " ")}
        </span>
        <div style={{ flex: 1 }} />
        {onOpenCost && (
          <button type="button" className="btn btn-ghost" onClick={onOpenCost} data-testid="cost-widget-open-full" style={{ fontSize: 11 }}>
            Open full cost tab <ArrowRight size={12} />
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        <Stat label="TOTAL" value={inr(data.total)} highlight />
        <Stat label="SUBTOTAL" value={inr(data.subtotal)} />
        <Stat label="GST" value={inr(data.gst)} />
        <Stat label="CONTINGENCY" value={inr(data.contingency)} />
        <Stat label="₹ / sqm" value={inr(data.cost_per_sqm)} />
        <Stat label="BOQ DELIVERABLES" value={String(data.boq_deliverables ?? 0)} />
      </div>

      {bench && (
        <div
          data-testid="cost-widget-bench"
          style={{
            padding: 10, borderRadius: 4, background: "var(--pf-surface-2)",
            border: `1px solid ${overBench ? "var(--pf-err)" : "var(--pf-ok)"}`,
            fontSize: 12.5, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          }}
        >
          {overBench ? <AlertTriangle size={13} color="var(--pf-err)" /> : <TrendingDown size={13} color="var(--pf-ok)" />}
          <strong style={{ color: overBench ? "var(--pf-err)" : "var(--pf-ok)" }}>
            {bench.verdict || (overBench ? "Above market median" : "Within / below market median")}
          </strong>
          <span style={{ color: "var(--pf-text-muted)" }}>
            Δ {bench.delta_pct == null ? "—" : `${bench.delta_pct > 0 ? "+" : ""}${bench.delta_pct}%`} vs {inr(bench.median)} median
            {bench.range_low != null && bench.range_high != null && (
              <>  · range {inr(bench.range_low)} – {inr(bench.range_high)}</>
            )}
          </span>
        </div>
      )}

      {data.top5_line_items?.length > 0 && (
        <div>
          <div className="pf-overline" style={{ marginBottom: 6 }}>TOP-5 COST HEADS</div>
          <table className="pf-mono" style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: "var(--pf-text-muted)" }}>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>Item</th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>Disc</th>
                <th style={{ textAlign: "right", padding: "4px 6px" }}>Qty</th>
                <th style={{ textAlign: "right", padding: "4px 6px" }}>Rate</th>
                <th style={{ textAlign: "right", padding: "4px 6px" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.top5_line_items.map((li, i) => (
                <tr key={i} data-testid={`cost-widget-row-${i}`} style={{ borderTop: "1px solid var(--pf-border-muted)" }}>
                  <td style={{ padding: "4px 6px" }}>{li.description}</td>
                  <td style={{ padding: "4px 6px", color: "var(--pf-accent)" }}>{li.discipline}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>{li.qty} {li.unit}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right" }}>{inr(li.rate)}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>{inr(li.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.market_summary && (
        <div
          data-testid="cost-widget-market"
          style={{ padding: 8, fontSize: 11, color: "var(--pf-text-muted)", borderTop: "1px dashed var(--pf-border-muted)" }}
        >
          <span style={{ marginRight: 10 }}>
            <TrendingUp size={10} color="var(--pf-err)" /> Rising: {data.market_summary.rising_items?.slice(0, 3).join(", ") || "—"}
          </span>
          <span>
            <TrendingDown size={10} color="var(--pf-ok)" /> Falling: {data.market_summary.falling_items?.slice(0, 3).join(", ") || "—"}
          </span>
          <span style={{ float: "right" }}>as of {data.market_summary.as_of}</span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div style={{ padding: 8, borderRadius: 4, background: "var(--pf-surface-2)" }}>
      <div className="pf-overline" style={{ fontSize: 9 }}>{label}</div>
      <div
        style={{
          fontSize: 14, fontWeight: 700, marginTop: 2,
          color: highlight ? "var(--pf-spark)" : "var(--pf-text)",
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        {value}
      </div>
    </div>
  );
}
